<template>
    <div class="py-6 px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="sm:flex sm:items-center sm:justify-between mb-6">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Ticket Categories</h1>
                        <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
                            Manage helpdesk ticket categories and subcategories
                        </p>
                    </div>
                    <InfoButton title="Ticket Categories Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            Ticket Categories organize helpdesk tickets into logical groups like Hardware Issues, Software Support,
                            Network Problems, or Access Requests. These categories help organize the ticket catalog, enable filtering,
                            and support reporting by ticket type. Categories can be hierarchical with parent and child categories,
                            and can be linked to SLA policies for automated response time management.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Add Category:</strong> Click "Add Category" to create a new ticket category with name
                              and optional parent category<br>
                            • <strong>Search:</strong> Filter categories by name using the search box<br>
                            • <strong>Status Filter:</strong> View all categories, only active ones, or only inactive ones<br>
                            • <strong>Tickets Column:</strong> Shows how many tickets are assigned to each category<br>
                            • <strong>Edit:</strong> Modify category name, description, or parent category<br>
                            • <strong>Activate/Deactivate:</strong> Toggle category status - inactive categories are hidden from
                              ticket creation<br>
                            • <strong>Delete:</strong> Only available if the category has no associated tickets
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Start simple</strong> - Create broad categories first (Hardware, Software, Network),
                              then add subcategories<br>
                            • <strong>Consistent naming</strong> - Use clear, standard terms for easy recognition<br>
                            • <strong>Deactivate not delete</strong> - Inactive categories preserve history while hiding
                              from new tickets<br>
                            • <strong>Plan for SLAs</strong> - Categories can be linked to SLA policies, so organize by
                              response time requirements<br>
                            • <strong>Ticket count</strong> shows adoption - categories with many tickets indicate commonly
                              reported issues
                        </p>
                    </InfoButton>
                </div>
                <div class="mt-4 sm:mt-0" v-if="canManageCategories">
                    <Link
                        :href="route('settings.tickets.categories.create')"
                        class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Category
                    </Link>
                </div>
            </div>

            <!-- Filters -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg mb-6 border border-gray-200 dark:border-gray-700">
                <div class="px-6 py-4">
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <!-- Search -->
                        <div>
                            <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Search</label>
                            <input
                                id="search"
                                v-model="search"
                                type="text"
                                placeholder="Search categories..."
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                        </div>

                        <!-- Status Filter -->
                        <div>
                            <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                            <select
                                id="status"
                                v-model="activeFilter"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">All Statuses</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <!-- Reset Filters -->
                    <div class="mt-4 flex justify-end" v-if="hasFilters">
                        <button
                            @click="resetFilters"
                            class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>
            </div>

            <!-- Categories Table -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Category Name
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Parent Category
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Default Priority
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Tickets
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-if="categories.data.length === 0">
                                <td colspan="6" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                    <span v-if="!canManageCategories">No categories found.</span>
                                    <span v-else>
                                        No categories found.
                                        <Link :href="route('settings.tickets.categories.create')" class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                                            Create your first category
                                        </Link>
                                    </span>
                                </td>
                            </tr>
                            <tr v-for="category in categories.data" :key="category.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <!-- Category Name -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ category.name }}</div>
                                    <div v-if="category.description" class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                        {{ category.description }}
                                    </div>
                                </td>

                                <!-- Parent Category -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div v-if="category.parent" class="text-sm text-gray-900 dark:text-white">{{ category.parent.name }}</div>
                                    <div v-else class="text-sm text-gray-400 italic">None</div>
                                </td>

                                <!-- Default Priority -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            category.default_priority === 'low' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                            category.default_priority === 'normal' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                            category.default_priority === 'high' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                            category.default_priority === 'urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        ]"
                                    >
                                        {{ category.default_priority ? category.default_priority.charAt(0).toUpperCase() + category.default_priority.slice(1) : 'N/A' }}
                                    </span>
                                </td>

                                <!-- Tickets Count -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-white">{{ category.tickets_count || 0 }}</div>
                                </td>

                                <!-- Status -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            category.is_active
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        ]"
                                    >
                                        {{ category.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>

                                <!-- Actions -->
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div v-if="canManageCategories" class="flex items-center justify-end space-x-3">
                                        <Link
                                            :href="route('settings.tickets.categories.edit', category.id)"
                                            class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                            title="Edit"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            @click="toggleActive(category)"
                                            class="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                            :title="category.is_active ? 'Deactivate' : 'Activate'"
                                        >
                                            {{ category.is_active ? 'Deactivate' : 'Activate' }}
                                        </button>
                                        <button
                                            v-if="category.tickets_count === 0"
                                            @click="deleteCategory(category)"
                                            class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                            title="Delete"
                                        >
                                            Delete
                                        </button>
                                        <span
                                            v-else
                                            class="text-gray-400 cursor-not-allowed"
                                            title="Cannot delete category with tickets"
                                        >
                                            Delete
                                        </span>
                                    </div>
                                    <div v-else class="text-sm text-gray-400">
                                        No actions available
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="categories.data.length > 0" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <Pagination :links="categories.links" />
                </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { router, Link, usePage } from '@inertiajs/vue3';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    categories: {
        type: Object,
        required: true
    },
    filters: {
        type: Object,
        required: true
    },
    canManageCategories: {
        type: Boolean,
        required: true
    }
});

const toast = useToast();
const page = usePage();

// Filter state
const search = ref(props.filters.search || '');
const activeFilter = ref(props.filters.active || '');

// Computed property to check if any filters are active
const hasFilters = ref(false);

// Watch filters and update URL with debounce
let searchTimeout = null;

watch([search, activeFilter], () => {
    hasFilters.value = search.value || activeFilter.value;

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        router.get(route('settings.tickets.categories.index'), {
            search: search.value,
            active: activeFilter.value
        }, {
            preserveState: true,
            preserveScroll: true,
            replace: true
        });
    }, 300);
});

const resetFilters = () => {
    search.value = '';
    activeFilter.value = '';
};

const toggleActive = (category) => {
    if (confirm(`Are you sure you want to ${category.is_active ? 'deactivate' : 'activate'} this category?`)) {
        router.post(route('settings.tickets.categories.toggleActive', category.id), {
            _company_id: page.props.company_id
        }, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Category ${category.is_active ? 'deactivated' : 'activated'} successfully!`);
            },
            onError: () => {
                toast.error('Failed to update category status.');
            }
        });
    }
};

const deleteCategory = (category) => {
    if (confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
        router.delete(route('settings.tickets.categories.destroy', category.id), {
            data: {
                _company_id: page.props.company_id
            },
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Category deleted successfully!');
            },
            onError: (errors) => {
                toast.error(errors.error || 'Failed to delete category.');
            }
        });
    }
};
</script>
